import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowRight, Check, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const DemoModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    website: "",
    mobile: "",
    role: "",
    industry: "",
    teamSize: "",
    message: "",
  });

  const roles = [
    "MD / CEO",
    "CFO",
    "COO",
    "CIO / CTO",
    "SVP / EVP",
    "VP",
    "Director",
    "Head of Department",
    "Other"
  ];

  const industries = [
    "Manufacturing",
    "Financial Services",
    "Healthcare",
    "Retail / Consumer",
    "Construction / Real Estate",
    "Transportation / Logistics",
    "Professional Services",
    "Education",
    "Public Sector",
    "Energy / Utilities / Telecom",
    "Technology",
    "Other"
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.role || !formData.industry) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/demo/request`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        role: formData.role,
        industry: formData.industry,
        website: formData.website || null,
        mobile: formData.mobile || null,
        team_size: formData.teamSize || null,
        message: formData.message || null,
        source_page: "homepage",
      });
      setSubmitted(true);
      toast.success("Demo request submitted successfully!");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({ 
      name: "", email: "", company: "", website: "", mobile: "", 
      role: "", industry: "", teamSize: "", message: "" 
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="sm:max-w-lg bg-[#0F0F10] border border-white/10 text-white max-h-[90vh] overflow-y-auto"
        data-testid="demo-modal"
        data-lenis-prevent
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-['Outfit'] text-white">
            {submitted ? "Request Received" : "Request a Demo"}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            {submitted 
              ? "We'll be in touch shortly to schedule your personalized demo."
              : "See OnTenacity in action with a personalized walkthrough."}
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            {/* Name & Email Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="demo-name" className="text-[#FAFAFA]">Full Name *</Label>
                <Input
                  id="demo-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  className="form-input"
                  data-testid="demo-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="demo-email" className="text-[#FAFAFA]">Work Email *</Label>
                <Input
                  id="demo-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@company.com"
                  className="form-input"
                  data-testid="demo-email-input"
                />
              </div>
            </div>

            {/* Mobile & Role Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="demo-mobile" className="text-[#FAFAFA]">Mobile Number</Label>
                <Input
                  id="demo-mobile"
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="+91 98765 43210"
                  className="form-input"
                  data-testid="demo-mobile-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#FAFAFA]">Your Role *</Label>
                <Select value={formData.role} onValueChange={(value) => handleSelectChange("role", value)}>
                  <SelectTrigger className="form-input" data-testid="demo-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F10] border border-white/10">
                    {roles.map((role) => (
                      <SelectItem 
                        key={role} 
                        value={role}
                        className="text-[#FAFAFA] hover:bg-white/5 focus:bg-white/5"
                      >
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Company & Website Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="demo-company" className="text-[#FAFAFA]">Company *</Label>
                <Input
                  id="demo-company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Acme Corp"
                  className="form-input"
                  data-testid="demo-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="demo-website" className="text-[#FAFAFA]">Website</Label>
                <Input
                  id="demo-website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://company.com"
                  className="form-input"
                  data-testid="demo-website-input"
                />
              </div>
            </div>

            {/* Industry & Team Size Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#FAFAFA]">Industry *</Label>
                <Select value={formData.industry} onValueChange={(value) => handleSelectChange("industry", value)}>
                  <SelectTrigger className="form-input" data-testid="demo-industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F10] border border-white/10">
                    {industries.map((industry) => (
                      <SelectItem 
                        key={industry} 
                        value={industry}
                        className="text-[#FAFAFA] hover:bg-white/5 focus:bg-white/5"
                      >
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="demo-teamSize" className="text-[#FAFAFA]">Team Size</Label>
                <Input
                  id="demo-teamSize"
                  name="teamSize"
                  value={formData.teamSize}
                  onChange={handleInputChange}
                  placeholder="50-200"
                  className="form-input"
                  data-testid="demo-team-size-input"
                />
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="demo-message" className="text-[#FAFAFA]">
                What are you hoping to achieve?
              </Label>
              <textarea
                id="demo-message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your goals..."
                className="w-full h-20 px-3 py-2 rounded-md form-input resize-none"
                data-testid="demo-message-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                data-testid="demo-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary rounded-full px-6 flex items-center gap-2"
                data-testid="demo-submit-btn"
              >
                {loading ? (
                  <CircleNotch size={18} className="animate-spin" />
                ) : (
                  <>
                    Request Demo
                    <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00E5FF]/20 flex items-center justify-center">
              <Check size={32} className="text-[#00E5FF]" />
            </div>
            <p className="text-[#A1A1AA] text-sm">
              Thank you, {formData.name}. Our team will contact you at {formData.email} 
              to schedule your personalized OnTenacity demo.
            </p>
            <Button
              onClick={handleClose}
              className="mt-6 btn-primary rounded-full px-6"
              data-testid="demo-done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoModal;
